import type { DashboardData } from "@crewledger/shared/schemas";
import { Check, Circle } from "lucide-react";
import { cn } from "@/lib/utils";

interface PayPeriodProgressProps {
  steps: DashboardData["payPeriodProgress"];
  payPeriod: DashboardData["currentPayPeriod"];
  loading: boolean;
}

export function PayPeriodProgress({ steps, payPeriod, loading }: PayPeriodProgressProps) {
  if (loading) {
    return (
      <div className="rounded-xl border bg-card-white p-5">
        <div className="h-5 w-48 animate-pulse rounded bg-navy-deep/8" />
        <div className="mt-6 h-2 w-full animate-pulse rounded-full bg-navy-deep/8" />
      </div>
    );
  }

  if (!payPeriod) {
    return (
      <div className="rounded-xl border border-dashed bg-card-white p-6 text-center">
        <p className="font-semibold text-navy-deep">No pay period open</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Create a pay period to start daily entry and payroll workflow.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border bg-card-white p-4 shadow-[var(--shadow-card)] sm:p-5">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Pay period progress
          </h2>
          <p className="font-semibold text-navy-deep">{payPeriod.label}</p>
        </div>
        <span className="mt-1 inline-flex w-fit rounded-full bg-navy-deep/5 px-2.5 py-0.5 text-xs font-medium text-navy-blueprint sm:mt-0">
          {payPeriod.status.replaceAll("_", " ")}
        </span>
      </div>

      {/* Desktop horizontal stepper */}
      <ol className="mt-6 hidden md:flex md:items-center md:justify-between">
        {steps.map((step, index) => (
          <li key={step.key} className="relative flex flex-1 flex-col items-center">
            {index > 0 ? (
              <span
                className={cn(
                  "absolute right-1/2 top-3 h-0.5 w-full -translate-y-1/2",
                  step.state === "pending" ? "bg-border" : "bg-green-approved",
                )}
                aria-hidden
              />
            ) : null}
            <StepIcon state={step.state} />
            <span
              className={cn(
                "mt-2 text-center text-xs font-medium",
                step.state === "active" ? "text-orange-construction" : "text-muted-foreground",
              )}
            >
              {step.label}
            </span>
          </li>
        ))}
      </ol>

      {/* Mobile vertical stepper */}
      <ol className="mt-4 space-y-3 md:hidden">
        {steps.map((step) => (
          <li key={step.key} className="flex items-center gap-3">
            <StepIcon state={step.state} compact />
            <span
              className={cn(
                "text-sm font-medium",
                step.state === "active" ? "text-orange-construction" : "text-navy-deep",
              )}
            >
              {step.label}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function StepIcon({
  state,
  compact = false,
}: {
  state: "pending" | "active" | "complete";
  compact?: boolean;
}) {
  const size = compact ? "h-6 w-6" : "h-7 w-7";

  if (state === "complete") {
    return (
      <span
        className={cn(
          "relative z-10 flex items-center justify-center rounded-full bg-green-approved text-white",
          size,
        )}
      >
        <Check className="h-3.5 w-3.5" />
      </span>
    );
  }

  if (state === "active") {
    return (
      <span
        className={cn(
          "relative z-10 flex items-center justify-center rounded-full border-2 border-orange-construction bg-orange-50 ring-4 ring-orange-construction/20",
          size,
        )}
      >
        <Circle className="h-2 w-2 fill-orange-construction text-orange-construction" />
      </span>
    );
  }

  return (
    <span
      className={cn(
        "relative z-10 flex items-center justify-center rounded-full border-2 border-border bg-bg-light",
        size,
      )}
    >
      <Circle className="h-2 w-2 text-border" />
    </span>
  );
}
